import { type GlJeLinesList } from '@store/Finance/Journals/JournalsModels';
import { useTranslation } from 'react-i18next';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import useGetCodeCombination from '@hooks/Finance/ChartAccountsDetails/useGetCodeCombination';
import { useMemo } from 'react';
import { Card } from '@components/card/card';

interface Props {
  JournalLine: GlJeLinesList[];
  CategoryName: string;
}

const Lines = ({ JournalLine, CategoryName }: Props) => {
  const { t } = useTranslation();

  const { GetCodeCombination, GetCodeCombinationDetails } = useGetCodeCombination();

  // حساب الإجماليات
  const { totalAccountDr, totalAccountCr, balance } = useMemo(() => {
    const dr = JournalLine?.reduce((total, line) => total + (Number(line.enteredDr) || 0), 0);
    const cr = JournalLine?.reduce((total, line) => total + (Number(line.enteredCr) || 0), 0);
    return {
      totalAccountDr: dr,
      totalAccountCr: cr,
      balance: dr - cr
    };
  }, [JournalLine]);

  return (
    <div className='container-fluid p-3'>
      {/* شريط الإحصائيات */}
      <div className='row mb-2'>
        <div className='col-12 col-md-6 col-lg-3 mb-3'>
          <Card>
            <div className=' p-3 bg-secondary bg-opacity-15 h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <div className='text-muted small'>{t('إجمالي المدين')}</div>
                  <div className='text-danger fs-5 fw-bold'>{totalAccountDr.toLocaleString()}</div>
                </div>
                <div className='text-danger'>
                  <i className='bi bi-arrow-up-circle fs-4'></i>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className='col-12 col-md-6 col-lg-3 mb-3'>
          <Card>
            <div className=' p-3 bg-secondary bg-opacity-15 h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <div className='text-muted small'>{t('إجمالي الدائن')}</div>
                  <div className='text-success fs-5 fw-bold'>{totalAccountCr.toLocaleString()}</div>
                </div>
                <div className='text-success'>
                  <i className='bi bi-arrow-down-circle fs-4'></i>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className='col-12 col-md-12 col-lg-6 mb-3'>
          <Card>
            <div className=' p-3 bg-secondary bg-opacity-15 h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <div className='text-muted small'>{t('تصنيف القيد')}</div>
                  <div className='fs-5 fw-bold'>{CategoryName}</div>
                </div>
                <div className='text-info'>
                  <i className='bi bi-tag fs-4'></i>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* جدول البنود */}
      <Card>
        <div className='table-responsive   shadow-sm'>
          <table className='table table-hover mb-0'>
            <thead className='bg-theme bg-opacity-25'>
              <tr>
                <th className='text-center align-middle' style={{ width: '5%' }}>
                  #
                </th>
                <th className='align-middle text-center' style={{ width: '10%' }}>
                  {t('مدين')}
                </th>
                <th className='align-middle text-center' style={{ width: '10%' }}>
                  {t('دائن')}
                </th>
                {/* <th className='align-middle text-center' style={{ width: '10%' }}>
                  {t('الإجمالي')}
                </th> */}
                <th className='align-middle' style={{ width: '35%' }}>
                  {t('الوصف')}
                </th>
                <th className='align-middle' style={{ width: '30%' }}>
                  {t('الحســـاب')}
                </th>
              </tr>
            </thead>

            <tbody>
              {JournalLine.length > 0 ? (
                [...JournalLine] // نستخدم نسخة لتجنب تعديل الحالة الأصلية
                  .sort((a, b) => {
                    const aIsDebit = Number(a.accountDr) > 0;
                    const bIsDebit = Number(b.accountDr) > 0;

                    if (aIsDebit && !bIsDebit) return -1; // المدين أولاً
                    if (!aIsDebit && bIsDebit) return 1; // الدائن بعده
                    return 0; // إذا كانا من نفس النوع، لا تغيّر الترتيب
                  })
                  .map((line, index) => {
                    const amount = Number(line.accountCr) || Number(line.accountDr) || 0;
                    const isCredit = Number(line.accountCr) > 0;

                    return (
                      <tr key={index} className='align-middle'>
                        <td className='text-center text-muted'>{index + 1}</td>
                        <td className={`font-monospace text-center ${!isCredit ? 'text-danger' : 'text-muted'}`}>
                          {!isCredit ? amount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                        </td>
                        <td className={`font-monospace text-center ${isCredit ? 'text-success' : 'text-muted'}`}>
                          {isCredit ? amount.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                        </td>
                        {/* <td
                          className={`font-monospace text-center fw-bold ${isCredit ? 'text-success' : 'text-danger'}`}
                        >
                          {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td> */}
                        <td>{line.description || <span className='text-muted'>{t('لا يوجد وصف')}</span>}</td>
                        <td className='font-monospace'>
                          <PopoverCustom
                            placement='right'
                            childern={GetCodeCombination(
                              line.companyId,
                              line.branchId,
                              line.accountId,
                              line.costCenterId
                            )}
                            Header={t('تفاصيل الحـــساب')}
                            Body={GetCodeCombinationDetails(
                              line.companyId,
                              line.branchId,
                              line.accountId,
                              line.costCenterId
                            )}
                          />
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan={6} className='text-center py-4'>
                    <div className='py-3'>
                      <div className='fs-1'>📝</div>
                      <div className='mt-2 text-muted'>{t('لا توجد بنود لعرضها')}</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Lines;
